import { NSARouterMakerResult } from 'namirasoft-account-react';
import { FilterItem, FilterItemOperator, IStorageLocal, SortItem } from 'namirasoft-core';
import { NamirasoftPaymentMetaDatabase, OfferFullRow, ProductRow, SalesItemRow, SubscriptionPaidRow, SubscriptionValidation } from 'namirasoft-payment';
import { NamirasoftMap } from 'namirasoft-site-map';
import { BaseColumnFormatter, DateTimeFormatter, IDFormatter, NSColumn, NSLoading, NSNoData, NSPanel, NSSection, NSTable, NSTitle } from 'namirasoft-site-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PaymentLayout } from '../PaymentLayout';
import { PaymentProps } from '../PaymentProps';
import NSPOfferCard from '../components/NSPOfferCard';
import Styles from './OfferListPage.module.css';

export interface OfferListPageState
{
    product: ProductRow | null;
    sales_item: SalesItemRow | null;
    offers: OfferFullRow[];
    subscription_validation: SubscriptionValidation | null;
    isLoading: boolean;
}

export interface OfferListPageProps extends NSARouterMakerResult, PaymentProps { }

function OfferListPage(props: OfferListPageProps)
{
    let params = useParams();
    let namirasoft = new NamirasoftMap(props.account.token_manager, props.notifier.onError);
    let [state, setState] = useState<OfferListPageState>({
        product: null,
        sales_item: null,
        offers: [],
        subscription_validation: null,
        isLoading: true
    });

    useEffect(() =>
    {
        if (params.product_slug)
            loadProduct(params.product_slug);
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, []);

    function loadProduct(product_slug_id: string)
    {
        props.servers.payment.product.Get(product_slug_id)
            .then((product) =>
            {
                setState(prevState => ({ ...prevState, product }));

                if (product != null)
                    if (params.sales_item_slug)
                        loadSalesItem(product.id, params.sales_item_slug);
            })
            .catch(() => { });
    }

    async function loadSalesItem(product_id: string, slug: string)
    {
        let meta = new NamirasoftPaymentMetaDatabase(props.account.token_manager, props.notifier.onError);
        let filters = [
            new FilterItem(meta.tables.sales_item, meta.tables.sales_item.columns.product_id, false, FilterItemOperator.all.equals, product_id),
            new FilterItem(meta.tables.sales_item, meta.tables.sales_item.columns.slug, false, FilterItemOperator.all.equals, slug)
        ];

        let sales_item_list = await props.servers.payment.sales_item.List(filters, null, null, [], null);
        let sales_item = sales_item_list.rows[0] ?? null;

        if (sales_item == null)
        {
            setState(prevState => ({ ...prevState, sales_item, isLoading: false }));
            return;
        }

        let offers = await getOffers(product_id, sales_item.id, params.offer_id);
        let subscription_validation = await getSubscriptionValidation(sales_item.id);

        setState(prevState => ({
            ...prevState,
            sales_item,
            offers: offers.rows,
            subscription_validation,
            isLoading: false
        }));
    }

    async function getOffers(product_id: string, sales_item_id: string, offer_id?: string)
    {
        let meta = new NamirasoftPaymentMetaDatabase(props.account.token_manager, props.notifier.onError);
        let filters = [
            new FilterItem(meta.tables.offer, meta.tables.offer.columns.product_id, false, FilterItemOperator.all.equals, product_id),
            new FilterItem(meta.tables.offer, meta.tables.offer.columns.sales_item_id, false, FilterItemOperator.all.equals, sales_item_id)
        ];
        if (offer_id)
            filters.push(new FilterItem(meta.tables.offer, meta.tables.offer.columns.id, false, FilterItemOperator.all.equals, offer_id));

        let sorts = [
            new SortItem(meta.tables.offer, meta.tables.offer.columns.order, true),
        ];

        try
        {
            return await props.servers.payment.offer.List(filters, null, null, sorts, null);
        }
        catch (error)
        {
            return { count: 0, rows: [] }
        }
    }

    async function getSubscriptionValidation(sales_item_id: string)
    {
        try
        {
            return await props.servers.payment.subscription.GetValidation(sales_item_id);
        }
        catch (error)
        {
            return null;
        }
    }

    return (
        <PaymentLayout
            {...props}
            action={{
                title: "Offer List",
                description: 'A detailed list of offers to help you select the best option for your needs.'
            }}
        >
            <NSColumn style={{ width: "fit-content", alignItems: "center", margin: "0 auto" }}>
                {
                    (state.subscription_validation?.subscription_paids?.length ?? 0) > 0 &&
                    <NSPanel classList={["w-100"]}>
                        <NSTitle title='Current Plan' classList={["mb-0"]} />
                        <NSTable<SubscriptionPaidRow>
                            id={state.subscription_validation!.sales_item_id}
                            name={`subscirption-paid`}
                            checkbox={false}
                            columns={[
                                BaseColumnFormatter.getColumn(namirasoft.payment.tables.subscription_paid.columns.offer_id, new IDFormatter(namirasoft, new IStorageLocal())),
                                BaseColumnFormatter.getColumn(namirasoft.payment.tables.subscription_paid.columns.expires_at, new DateTimeFormatter())
                            ]}
                            getRows={async () =>
                            {
                                let rows = state.subscription_validation!.subscription_paids!;
                                rows = rows.filter(r => new Date(r.expires_at).getTime() > Date.now());
                                return { rows, count: rows.length }
                            }}
                            getRowKey={e => e.value.id.toString()}
                            style={{ width: "100%" }}
                            ui={{
                                pagination: false,
                                button_panel: false,
                            }}
                            classList={["h-auto"]}
                        />
                    </NSPanel>
                }
                {
                    state.offers.length !== 0 ?
                        <div className={Styles.ns_offers_group_card}>
                            {
                                state.offers.map((offer) =>
                                (
                                    <NSPOfferCard
                                        key={offer.id}
                                        {...props}
                                        offer={offer}
                                        offer_items={offer.offer_item}
                                        subscription={offer.subscription}
                                    />
                                ))
                            }
                        </div>
                        :
                        <NSSection>
                            {
                                state.isLoading
                                    ?
                                    <NSLoading />
                                    :
                                    <NSNoData
                                        classList={["p-3"]}
                                        lable='Currently, there are no offers available. Please check back later for updates.'
                                    />
                            }
                        </NSSection>
                }
            </NSColumn>
        </PaymentLayout >
    );
}

export default OfferListPage;